import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Briefcase,
  ChevronRight,
  Coins,
  FileText,
  FolderCheck,
  GraduationCap,
  Inbox,
  MessageSquare,
  Receipt,
  Shield,
  Sparkles,
  UserCircle,
  Wrench,
} from "lucide-react";
import { WorkspaceShell, WorkspaceSupportBlock } from "../../components/workspace/WorkspaceShell";
import PccSearch from "../../components/workspace/PccSearch";

const FREQUENT_TOOLS = [
  { title: "Daily Care Log", icon: FileText, href: "/workspace/pro-carer-circle/shift-kit#daily-care-log" },
  { title: "Daily Medication Record", icon: FileText, href: "/workspace/pro-carer-circle/shift-kit#medication" },
  { title: "Incident Report", icon: FileText, href: "/workspace/pro-carer-circle/shift-kit#incident-report" },
  { title: "Invoice Generator", icon: Receipt, href: "/workspace/tools/tax-confidence-kit#invoice-generator" },
  { title: "End of Shift Summary", icon: FileText, href: "/workspace/pro-carer-circle/shift-kit#end-of-shift" },
  { title: "Care Agreement", icon: FileText, href: "/workspace/pro-carer-circle/contracts-agreements" },
];

const TOPICS = [
  { slug: "pricing-income", title: "Pricing & Income", body: "Set a sustainable hourly or day rate.", icon: Coins },
  { slug: "tax-admin", title: "Tax & Admin", body: "Track income, expenses and invoices.", icon: Receipt },
  { slug: "payment-protection", title: "Payment Protection", body: "Get paid properly. Avoid disputes.", icon: Shield },
  { slug: "contracts-agreements", title: "Contracts & Agreements", body: "Ready-to-use templates for every placement.", icon: FileText },
  { slug: "working-independently", title: "Working Independently", body: "Boundaries, communication and saying no.", icon: Briefcase },
  { slug: "shift-kit", title: "Shift Kit", body: "Your everyday on-shift forms.", icon: Wrench },
  { slug: "safety-escalation", title: "Safety & Escalation", body: "Manual handling, incidents and safeguarding.", icon: Shield },
  { slug: "training-cpd", title: "Training & CPD", body: "Bundles, providers and a clear path.", icon: GraduationCap },
];

export default function ProCarerCircleHub() {
  return (
    <WorkspaceShell
      testId="hub-pro-carer-circle"
      eyebrow="Pro Carer Office"
      memberBadge="Pro Carer member"
      accent="teal"
      title="Your Professional Workspace"
      intro="Everything for working independently, professionally and with confidence — payment protection, contracts, pricing, your shift kit and training, all in one place."
    >
      {/* ── Start Here ────────────────────────────────────────────────── */}
      <Link
        to="/workspace/pro-carer-circle/start-here"
        data-testid="pro-start-here-link"
        className="group flex items-center justify-between gap-4 rounded-2xl border border-teal/20 bg-teal/5 p-5 hover:border-teal transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-semibold">New here? Follow the Start Here guide</h3>
            <p className="text-sm text-ink-muted">A short walk-through of your workspace and where everything lives.</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-teal shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </Link>

      {/* ── The Professional Carer Advantage Series ────────────────────── */}
      <Link
        to="/workspace/pro-carer-circle/advantage-series"
        data-testid="pro-advantage-series-link"
        className="group flex items-center justify-between gap-4 rounded-2xl border border-amber-300 bg-amber-50 p-5 hover:border-amber-500 transition-colors min-w-0"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-semibold flex items-center gap-2 flex-wrap">
              The Professional Carer Advantage Series
              <span className="pill text-[11px] bg-amber-500 text-white shrink-0">New</span>
            </h3>
            <p className="text-sm text-ink-muted">Members-only articles to help you grow your independent care practice.</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-amber-600 shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </Link>

      {/* ── My professional workspace ─────────────────────────────────── */}
      <section data-testid="pro-my-workspace" className="min-w-0">
        <h2 className="font-display text-xl font-semibold mb-4">My professional workspace</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          <Link
            to="/workspace/pro-carer-profile"
            data-testid="pro-profile-cta"
            className="rounded-2xl border border-teal/20 bg-white p-6 hover:shadow-md transition-shadow group min-w-0"
          >
            <UserCircle className="w-8 h-8 text-teal mb-3" strokeWidth={1.75} />
            <h3 className="font-display font-semibold">My Pro Carer Profile</h3>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">Present yourself professionally for private placements.</p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-teal">
              Open profile <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>

          <Link
            to="/workspace/carer-profile/enquiries"
            data-testid="pro-profile-enquiries-cta"
            className="rounded-2xl border border-teal/20 bg-white p-6 hover:shadow-md transition-shadow group min-w-0"
          >
            <Inbox className="w-8 h-8 text-teal mb-3" strokeWidth={1.75} />
            <h3 className="font-display font-semibold">Enquiries & Invitations</h3>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">Private enquiries families have sent to your profile.</p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-teal">
              Open enquiries <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>

          <Link
            to="/workspace/carer-essentials"
            data-testid="pro-carer-essentials-cta"
            className="rounded-2xl border border-teal/20 bg-white p-6 hover:shadow-md transition-shadow group min-w-0"
          >
            <FolderCheck className="w-8 h-8 text-teal mb-3" strokeWidth={1.75} />
            <h3 className="font-display font-semibold">Carer Essentials</h3>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">Your documents, expiries, training, references and work record — all private and organised.</p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-teal">
              Open Carer Essentials <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>

          <div
            data-testid="pro-carer-rooms-card"
            className="sm:col-span-2 rounded-2xl border border-teal/20 bg-white p-6 min-w-0"
          >
            <MessageSquare className="w-8 h-8 text-teal mb-3" strokeWidth={1.75} />
            <h3 className="font-display font-semibold">Care Discussion Rooms</h3>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">
              A private space with a family to discuss care arrangements and keep conversations together.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href="/care-rooms"
                data-testid="pro-carer-view-rooms-btn"
                className="inline-flex items-center gap-2 rounded-xl border border-teal/30 bg-white px-4 py-2 text-sm font-semibold text-teal hover:bg-teal/5 transition-colors"
              >
                View My Care Rooms <ChevronRight className="w-4 h-4" strokeWidth={2} />
              </a>
              <a
                href="/care-discussion-room/new-carer"
                data-testid="pro-carer-create-room-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-teal px-4 py-2 text-sm font-semibold text-white hover:bg-teal/90 transition-colors"
              >
                Create a Care Discussion Room <ChevronRight className="w-4 h-4" strokeWidth={2} />
              </a>
            </div>
          </div>

          <Link
            to="/workspace/pro-carer-circle/care-opportunities"
            data-testid="pro-care-opportunities-cta"
            className="sm:col-span-2 rounded-2xl border border-teal/20 bg-white p-6 hover:shadow-md transition-shadow group min-w-0"
          >
            <Briefcase className="w-8 h-8 text-teal mb-3" strokeWidth={1.75} />
            <h3 className="font-display font-semibold">Browse Care Opportunities</h3>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">See families currently looking for independent care.</p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-teal">
              Browse opportunities <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>
        </div>
      </section>

      {/* ── Find a tool or resource ───────────────────────────────────── */}
      <section data-testid="pro-find-tool" className="min-w-0">
        <h2 className="font-display text-xl font-semibold mb-1">Find a tool or resource</h2>
        <p className="text-sm text-ink-muted mb-4">Search by name, topic or keyword — e.g. medication, invoice, incident, day rate.</p>
        <PccSearch />
      </section>

      {/* ── Frequently used tools ─────────────────────────────────────── */}
      <section data-testid="pro-frequent-tools" className="min-w-0">
        <h2 className="font-display text-xl font-semibold mb-4">Frequently used tools</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FREQUENT_TOOLS.map((t) => (
            <Link
              key={t.href}
              to={t.href}
              data-testid={`pro-frequent-tool-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-teal hover:shadow-sm transition-all min-w-0"
            >
              <div className="w-9 h-9 rounded-lg bg-teal/10 text-teal flex items-center justify-center shrink-0">
                <t.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </div>
              <span className="font-medium text-sm text-ink truncate">{t.title}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Browse by topic ───────────────────────────────────────────── */}
      <section data-testid="pro-browse-topics" className="min-w-0">
        <h2 className="font-display text-xl font-semibold mb-4">Browse by topic</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TOPICS.map((t) => (
            <Link
              key={t.slug}
              to={`/workspace/pro-carer-circle/${t.slug}`}
              data-testid={`pro-topic-card-${t.slug}`}
              className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-purple hover:shadow-md transition-all min-w-0"
            >
              <div className="w-9 h-9 rounded-lg bg-purple/10 text-purple flex items-center justify-center mb-3">
                <t.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </div>
              <div className="font-display font-semibold text-sm">{t.title}</div>
              <p className="mt-1 text-xs text-ink-muted leading-relaxed">{t.body}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-purple">
                Open <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <WorkspaceSupportBlock />

      {/* Footer disclaimer — kept once, not repeated on every category page */}
      <section data-testid="pro-disclaimer" className="rounded-3xl border border-slate-200 bg-[#f7f7f3] p-6 md:p-8">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
          <p className="text-xs md:text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Just Care Community is not a care agency,</strong> does not employ or manage carers, and does not provide regulated care services. Our resources are designed to support clearer, safer and more professional independent care arrangements. JCC templates and tools are general guidance only and are not a substitute for independent legal, tax, employment, financial, medical, clinical, safeguarding or professional advice. In an emergency, contact the appropriate emergency service without delay.
          </p>
        </div>
      </section>
    </WorkspaceShell>
  );
}
